import type { TrackingFn } from '@change-corgi/core/react/tracking';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { trackShareCancel, trackShareFailure, trackShareIntent, trackShareSuccess } from './track';

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: {
		intent: string;
		success: string;
		failure: string;
		cancel: string;
	};
	mode?: { mobile: 'sdk' | 'url'; desktop: 'sdk' | 'url' };
};

async function shareWithUrl(
	url: string,
	options: Options,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<boolean> {
	const { trackingEvents, trackingData } = options;
	const { errorReporter, facebook } = utilityContext;
	try {
		const shareUrl = new URL('https://www.facebook.com/dialog/share');
		shareUrl.searchParams.append('app_id', facebook.appId);
		shareUrl.searchParams.append('href', url);
		await Promise.resolve(window.open(shareUrl.toString(), '_blank'));
		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);
		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}

type ShareWithSdkParams = {
	url: string;
	options: Options;
	utilityContext: UtilityContext;
	track: TrackingFn;
	isDesktop: boolean;
};

async function shareWithSdk({ url, options, utilityContext, track, isDesktop }: ShareWithSdkParams): Promise<boolean> {
	const { trackingEvents, trackingData } = options;
	const { errorReporter, facebook } = utilityContext;
	try {
		const response = await facebook.ui({
			method: 'share',
			href: url,
			// the SDK defaults to iframe on desktop when the user
			// is logged in with their FB account
			...(isDesktop && { display: 'popup' }),
		});
		if (response.cancelled) {
			trackShareCancel(trackingEvents.cancel, trackingData, utilityContext, track);
			return false;
		}
		trackShareSuccess(trackingEvents.success, trackingData, utilityContext, track);
		return true;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		void errorReporter.report(new Error(`Could not share to facebook: ${e}`));
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		trackShareFailure(trackingEvents.failure, { error: e.toString() }, trackingData, utilityContext, track);
		return false;
	}
}

export async function shareFacebook(
	url: string,
	options: Options,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<boolean> {
	// Default mode to 'sdk' for both mobile and desktop if not provided
	const { mode = { mobile: 'sdk', desktop: 'sdk' }, trackingEvents, trackingData } = options;
	trackShareIntent(trackingEvents.intent, trackingData, utilityContext, track);

	const isDesktop = !['mobile', 'tablet'].includes(
		utilityContext.userAgent.getParsedUserAgent().getDevice().type || '',
	);

	const currentMode = isDesktop ? mode.desktop : mode.mobile;

	if (currentMode === 'sdk') {
		return shareWithSdk({ url, options, utilityContext, track, isDesktop });
	}

	return shareWithUrl(url, options, utilityContext, track);
}
